import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import apiUrls from "src/api";
import { useAppDispatch } from "src/common/state/hooks";
import { setAlert } from "src/features/alert/state/alertsSlice";
import { fetchAndUpdateSpecificConversation } from "../../state/chatbotSlice";

const useRetryImageAnalysis = (conversationId: number | undefined, onSynced?: () => void) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [isRetrying, setIsRetrying] = useState(false);
  const CG = "pages.chat.configureGeneration";

  const retry = useCallback(async () => {
    if (!conversationId || isRetrying) return;
    setIsRetrying(true);
    try {
      const jwt = localStorage.getItem("wixar.auth.jwt");
      const resp = await fetch(apiUrls.gpt.planMode.retryImageDescriptions, {
        method: "POST",
        headers: { Authorization: `Bearer ${jwt}`, "Content-Type": "application/json" },
        body: JSON.stringify({ conversationId }),
      });
      if (!resp.ok) throw new Error(`retry-image-descriptions ${resp.status}`);
      const json = (await resp.json()) as { stillFailed?: boolean };
      if (json.stillFailed) {
        dispatch(
          setAlert({
            msg: t(`${CG}.imageAnalysisRetryFailed`),
            type: "warning",
            isOpen: true,
            showCloseButton: true,
          }),
        );
      }
      await dispatch(fetchAndUpdateSpecificConversation({ conversationId }));
      onSynced?.();
    } catch {
      dispatch(
        setAlert({
          msg: t(`${CG}.imageAnalysisRetryFailed`),
          type: "error",
          isOpen: true,
          showCloseButton: true,
        }),
      );
    } finally {
      setIsRetrying(false);
    }
  }, [conversationId, isRetrying, dispatch, t, onSynced]);

  return { isRetrying, retry };
};

export default useRetryImageAnalysis;
